import { QRCodeSVG } from 'qrcode.react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui';
import CollapsibleTile from './CollapsibleTile';
import type { LiveLessonQuizState } from '../../lib/liveLessonTypes';

type LessonQuizPanelProps = {
  topic: string;
  onTopicChange: (value: string) => void;
  quizState: LiveLessonQuizState | null;
  joinUrl: string | null;
  hasDisplayConnection: boolean;
  loading: boolean;
  actionLoading: boolean;
  onGenerate: () => void;
  onStart: () => void;
  onAdvance: () => void;
  onFinish: () => void;
  onApproveStudent: (studentId: string) => void;
  onAllowExternalJoinChange: (next: boolean) => void;
};

function phaseLabel(phase: LiveLessonQuizState['session']['phase']) {
  if (phase === 'lobby') return 'Lobby';
  if (phase === 'question') return 'Frage läuft';
  if (phase === 'reveal') return 'Auflösung';
  return 'Beendet';
}

export default function LessonQuizPanel({
  topic,
  onTopicChange,
  quizState,
  joinUrl,
  hasDisplayConnection,
  loading,
  actionLoading,
  onGenerate,
  onStart,
  onAdvance,
  onFinish,
  onApproveStudent,
  onAllowExternalJoinChange
}: LessonQuizPanelProps) {
  const phase = quizState?.session.phase || 'lobby';
  const status = quizState?.session.status || 'draft';
  const canGenerate = topic.trim().length >= 3 && !loading && !actionLoading;
  const canStart = Boolean(quizState?.questions?.length) && hasDisplayConnection && status === 'draft' && !actionLoading;
  const canAdvance =
    Boolean(quizState) && status !== 'draft' && phase !== 'finished' && !actionLoading && hasDisplayConnection;
  const canFinish = Boolean(quizState) && phase !== 'finished' && !actionLoading;
  const waitingJoined = (quizState?.participants || []).filter((entry) => entry.status === 'joined');
  const waitingPending = (quizState?.participants || []).filter(
    (entry) => String(entry.status || '').toLowerCase() === 'pending'
  );

  return (
    <CollapsibleTile title="Lernerfolgskontrolle" defaultOpen contentClassName="space-y-3">
      <div className="space-y-1">
        <label className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Quiz-Thema</label>
        <input
          type="text"
          value={topic}
          onChange={(event) => onTopicChange(event.target.value)}
          className="input-field w-full"
          placeholder="z. B. Photosynthese"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onGenerate} disabled={!canGenerate}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          10 Fragen generieren
        </Button>
        <Button size="sm" variant="outline" onClick={onStart} disabled={!canStart}>
          Quiz starten
        </Button>
        {status !== 'draft' ? (
          <Button size="sm" variant="outline" onClick={onAdvance} disabled={!canAdvance}>
            {phase === 'lobby' ? 'Starten' : phase === 'question' ? 'Auflösen' : phase === 'reveal' ? 'Weiter' : 'Weiter'}
          </Button>
        ) : null}
        <Button size="sm" variant="outline" onClick={onFinish} disabled={!canFinish}>
          Beenden
        </Button>
      </div>

      {!hasDisplayConnection ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">Quiz-Start erst nach Display-Kopplung möglich.</p>
      ) : null}

      {quizState ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-200">
          <p>
            Status: <strong>{phaseLabel(quizState.session.phase)}</strong> · Frage{' '}
            {Math.min((quizState.currentQuestion?.questionIndex ?? quizState.session.currentQuestionIndex) + 1, Math.max(1, quizState.questions.length))}/
            {Math.max(1, quizState.questions.length)}
          </p>
          <p>Teilnehmende: {quizState.participants.filter((entry) => entry.status === 'joined').length}</p>
        </div>
      ) : null}

      {quizState ? (
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={quizState.session.allowExternalJoin}
            onChange={(event) => onAllowExternalJoinChange(event.target.checked)}
          />
          Externe Join-Anfragen direkt zulassen
        </label>
      ) : null}

      {status !== 'draft' && phase === 'lobby' ? (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 dark:border-sky-700/40 dark:bg-sky-900/20">
          <p className="text-sm font-semibold text-sky-900 dark:text-sky-100">Lobby aktiv</p>
          <p className="mt-1 text-xs text-sky-800 dark:text-sky-200">
            Schüler können jetzt beitreten. Mit <strong>Starten</strong> beginnt Frage 1.
          </p>
          <div className="mt-3">
            <Button size="sm" onClick={onAdvance} disabled={!canAdvance}>
              Starten
            </Button>
          </div>
        </div>
      ) : null}

      {joinUrl && status !== 'draft' ? (
        <div className="rounded-md border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900/60">
          <p className="mb-2 text-xs text-gray-600 dark:text-gray-300">Schüler-Join per QR-Code</p>
          <div className="flex items-center gap-3">
            <QRCodeSVG value={joinUrl} size={92} />
            <p className="break-all text-[11px] text-gray-500 dark:text-gray-400">{joinUrl}</p>
          </div>
        </div>
      ) : null}

      {waitingPending.length ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 dark:border-amber-600/40 dark:bg-amber-900/20">
          <p className="mb-1 text-xs font-semibold text-amber-800 dark:text-amber-200">Wartende Join-Anfragen</p>
          <div className="space-y-1">
            {waitingPending.map((entry) => (
              <div key={entry.studentId} className="flex items-center justify-between gap-2">
                <span className="truncate text-xs text-amber-900 dark:text-amber-100">{entry.displayName}</span>
                <Button size="sm" variant="outline" onClick={() => onApproveStudent(entry.studentId)} disabled={actionLoading}>
                  Freigeben
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {status !== 'draft' && phase === 'lobby' ? (
        <div className="rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900/60">
          <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300">Schüler warten in der Lobby</p>
          {waitingJoined.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">Noch keine Schüler beigetreten.</p>
          ) : (
            waitingJoined.map((entry) => (
              <div key={entry.studentId} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
                <span>{entry.displayName}</span>
                <span className="text-gray-400">wartet</span>
              </div>
            ))
          )}
        </div>
      ) : null}

      {quizState?.leaderboard?.length ? (
        <div className="rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900/60">
          <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300">Zwischenstand</p>
          {quizState.leaderboard.slice(0, 5).map((entry, idx) => (
            <div key={`${entry.name}-${idx}`} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
              <span>{idx + 1}. {entry.name}</span>
              <span>{entry.score}</span>
            </div>
          ))}
        </div>
      ) : null}
    </CollapsibleTile>
  );
}
